import type { CategoryCriteria, Movie, SortKey } from "@/types";

export function matchesCriteria(movie: Movie, criteria: CategoryCriteria): boolean {
  if (criteria.decade && movie.decade !== criteria.decade) return false;
  if (criteria.genre && !movie.genre.includes(criteria.genre)) return false;
  if (criteria.director && movie.director !== criteria.director) return false;
  if (typeof criteria.minRating === "number" && movie.rating < criteria.minRating) return false;
  return true;
}

export function filterByCriteria(list: Movie[], criteria: CategoryCriteria): Movie[] {
  return list.filter((movie) => matchesCriteria(movie, criteria));
}

export function searchMovies(list: Movie[], query: string): Movie[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return list;
  return list.filter((movie) => {
    const haystack = [
      movie.title,
      movie.titleEn,
      movie.director,
      movie.synopsis,
      movie.plot,
      String(movie.year),
      ...movie.cast,
      ...movie.genre,
      ...movie.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });
}

export function sortMovies(list: Movie[], sortKey: SortKey): Movie[] {
  const result = [...list];
  if (sortKey === "latest") {
    result.sort((a, b) => b.year - a.year);
  } else if (sortKey === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    // popular: featured first, then rating
    result.sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return b.rating - a.rating;
    });
  }
  return result;
}

export function getRelatedMovies(list: Movie[], current: Movie, limit = 4): Movie[] {
  const scored = list
    .filter((movie) => movie.id !== current.id)
    .map((movie) => {
      let score = 0;
      if (movie.director === current.director) score += 3;
      if (movie.decade === current.decade) score += 1;
      score += movie.genre.filter((g) => current.genre.includes(g)).length * 2;
      score += movie.tags.filter((t) => current.tags.includes(t)).length;
      return { movie, score };
    })
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, limit).map((item) => item.movie);
}

export function getAllDecades(list: Movie[]): string[] {
  return Array.from(new Set(list.map((movie) => movie.decade))).sort();
}

export function getAllDirectors(list: Movie[]): string[] {
  return Array.from(new Set(list.map((movie) => movie.director)));
}

export function getAllGenres(list: Movie[]): string[] {
  return Array.from(new Set(list.flatMap((movie) => movie.genre)));
}